import React, { useId } from 'react';
import PropTypes from 'prop-types';
import CheckboxGroup from './CheckboxGroup';
import {
  DEFAULT_PAYMENT_FAILURE_TRIGGER,
  MAX_DEDUP_WINDOW_HOURS,
  MAX_EXPIRY_MINUTES,
  PAYMENT_FAILURE_CONDITION,
  PAYMENT_FAILURE_TRIGGER_OPTIONS,
  can_be_instant,
  clamp_optional_int,
  default_instant_expiry_minutes,
  with_instant
} from './gen_config_utils';
import { format_duration_minutes } from '../../util_components/instant_alert/instant_timer';

const INPUT_CLASS =
  'rounded-md border border-line bg-surface-card px-2 py-1 text-sm text-content-primary';

const LABEL_STYLE = { color: 'var(--text-muted)', marginBottom: 0 };

// Instant tier for the membership-event conditions: rows are created the moment
// the event fires, pop up for RETENTION users, and auto-complete if unclaimed.
function InstantControls({ rule, meta, saving, onChange }) {
  const id = useId();
  const set_number = (field, raw, max) => {
    const v = clamp_optional_int(raw, 0, max);
    if (v === null) return;
    onChange({ ...rule, [field]: v });
  };
  const blank = (v) => v === '' || v === null || v === undefined;
  const expiry = blank(rule.expiry_minutes) ? '' : rule.expiry_minutes;
  const default_expiry = default_instant_expiry_minutes(meta);
  const dedup = blank(rule.dedup_window_hours) ? '' : rule.dedup_window_hours;
  const trigger_options =
    meta.payment_failure_trigger_options && meta.payment_failure_trigger_options.length
      ? meta.payment_failure_trigger_options
      : PAYMENT_FAILURE_TRIGGER_OPTIONS;
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={`${id}-instant`}
        className="inline-flex items-center gap-2 text-xs font-semibold cursor-pointer"
        style={LABEL_STYLE}
        title="Created the moment the membership event happens, alerted to retention, and auto-completed if nobody claims it in time."
      >
        <input
          id={`${id}-instant`}
          type="checkbox"
          checked={!!rule.is_instant}
          disabled={saving}
          onChange={(e) => onChange(with_instant(rule, e.target.checked, meta))}
        />
        Instant
      </label>
      {rule.is_instant ? (
        <div className="flex flex-wrap items-center gap-4">
          <label
            htmlFor={`${id}-expiry`}
            className="inline-flex items-center gap-1.5 text-xs"
            style={LABEL_STYLE}
          >
            Expiry
            <input
              id={`${id}-expiry`}
              type="number"
              min={0}
              max={MAX_EXPIRY_MINUTES}
              value={expiry}
              placeholder={String(default_expiry)}
              disabled={saving}
              onChange={(e) => set_number('expiry_minutes', e.target.value, MAX_EXPIRY_MINUTES)}
              className={INPUT_CLASS}
              style={{ width: 90 }}
              title={`Unclaimed items auto-complete after this many minutes. Blank = ${default_expiry}; 0 = never expires.`}
            />
            minutes (0 = never expires)
          </label>
          <label
            htmlFor={`${id}-dedup`}
            className="inline-flex items-center gap-1.5 text-xs"
            style={LABEL_STYLE}
          >
            Dedup window
            <input
              id={`${id}-dedup`}
              type="number"
              min={0}
              max={MAX_DEDUP_WINDOW_HOURS}
              value={dedup}
              placeholder="1"
              disabled={saving}
              onChange={(e) =>
                set_number('dedup_window_hours', e.target.value, MAX_DEDUP_WINDOW_HOURS)
              }
              className={INPUT_CLASS}
              style={{ width: 80 }}
              title="A repeat event for the same member inside this window creates no new item. 0 = no window; blank = 1 hour."
            />
            hours
          </label>
          {expiry === 0 ? null : (
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              unclaimed after {format_duration_minutes(expiry || default_expiry)} → auto-completed
            </span>
          )}
        </div>
      ) : null}
      {rule.is_instant && rule.condition === PAYMENT_FAILURE_CONDITION ? (
        <label
          htmlFor={`${id}-trigger`}
          className="inline-flex items-center gap-1.5 text-xs"
          style={LABEL_STYLE}
        >
          Trigger on
          <select
            id={`${id}-trigger`}
            value={rule.payment_failure_trigger || DEFAULT_PAYMENT_FAILURE_TRIGGER}
            disabled={saving}
            onChange={(e) => onChange({ ...rule, payment_failure_trigger: e.target.value })}
            className={INPUT_CLASS}
          >
            {trigger_options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
}

InstantControls.propTypes = {
  rule: PropTypes.shape({
    condition: PropTypes.string,
    is_instant: PropTypes.bool,
    expiry_minutes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dedup_window_hours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    payment_failure_trigger: PropTypes.string
  }).isRequired,
  meta: PropTypes.shape({
    payment_failure_trigger_options: PropTypes.arrayOf(PropTypes.shape({})),
    default_expiry_minutes: PropTypes.number
  }).isRequired,
  saving: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired
};

// Inline editor for one custom generation rule: the condition type, the
// membership-signup window, and the three optional filter groups (prepay /
// plan / account status — ANDed across groups, ORed within a group).
function CustomRuleEditor({ rule, meta, saving, onChange }) {
  const filters = rule.filters || {};
  const set_filter = (key, value) => onChange({ ...rule, filters: { ...filters, [key]: value } });

  return (
    <div className="mt-3 rounded-md border border-line bg-surface-card px-3 py-2 flex flex-col gap-2">
      <div>
        <div className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>
          Condition
        </div>
        <select
          value={rule.condition}
          onChange={(e) => {
            const condition = e.target.value;
            // The server drops is_instant on other conditions; mirror it so the
            // card never shows an instant rule that will not save as one.
            onChange({
              ...rule,
              condition,
              is_instant: can_be_instant(condition, meta) && !!rule.is_instant
            });
          }}
          disabled={saving}
          className="w-full rounded-md border border-line bg-surface-card px-2 py-1.5 text-sm text-content-primary"
          style={{ maxWidth: 420 }}
        >
          {(meta.condition_options || []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      {can_be_instant(rule.condition, meta) ? (
        <InstantControls rule={rule} meta={meta} saving={saving} onChange={onChange} />
      ) : null}
      <div>
        <div className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>
          Membership signup
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(meta.signup_options || []).map((o) => {
            const is_sel = (filters.signup_window || 'all_time') === o.value;
            return (
              <div
                key={o.value}
                role="button"
                tabIndex={0}
                onClick={() => !saving && set_filter('signup_window', o.value)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !saving) {
                    e.preventDefault();
                    set_filter('signup_window', o.value);
                  }
                }}
                className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer select-none border ${
                  is_sel
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-surface-card text-content-secondary border-line hover:border-line-emphasis'
                }`}
              >
                {o.label}
              </div>
            );
          })}
        </div>
      </div>
      <CheckboxGroup
        label="Prepay type"
        options={meta.prepay_options || []}
        selected={filters.prepay_type || []}
        onChange={(v) => set_filter('prepay_type', v)}
        disabled={saving}
      />
      <CheckboxGroup
        label="Plan type"
        options={meta.plan_options || []}
        selected={filters.plan_type || []}
        onChange={(v) => set_filter('plan_type', v)}
        disabled={saving}
      />
      <CheckboxGroup
        label="Account status"
        options={meta.status_options || []}
        selected={filters.account_status || []}
        onChange={(v) => set_filter('account_status', v)}
        disabled={saving}
      />
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Filters are combined with AND; values within a group with OR. Generated checklists are named
        after this rule&apos;s summary.
      </div>
    </div>
  );
}

CustomRuleEditor.propTypes = {
  rule: PropTypes.shape({
    condition: PropTypes.string,
    is_instant: PropTypes.bool,
    filters: PropTypes.shape({
      signup_window: PropTypes.string,
      prepay_type: PropTypes.arrayOf(PropTypes.string),
      plan_type: PropTypes.arrayOf(PropTypes.string),
      account_status: PropTypes.arrayOf(PropTypes.string)
    })
  }).isRequired,
  meta: PropTypes.shape({
    instant_conditions: PropTypes.arrayOf(PropTypes.string),
    condition_options: PropTypes.arrayOf(PropTypes.shape({})),
    signup_options: PropTypes.arrayOf(PropTypes.shape({})),
    prepay_options: PropTypes.arrayOf(PropTypes.shape({})),
    plan_options: PropTypes.arrayOf(PropTypes.shape({})),
    status_options: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  saving: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired
};

export default CustomRuleEditor;
